import { useEffect, useRef, useState } from 'react';
import { GridLoader } from 'react-spinners';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretLeft, faCaretRight } from '@fortawesome/free-solid-svg-icons';

import { useTimer } from '@/shared/hooks/export';
import { Article } from '@/modules/articles/model/article';
import theme from '@/shared/theme/index.module.scss';

import { opinionsApi } from '@/modules/articles/api';
import DeleteBookmarkModal from './delete-bookmark-modal';
import { useSubscription, MessageServiceConstants, messageService } from '@/core/messaging/export';

import styles from './bookmark-list.module.css';
import ArticleModal from '@/modules/articles/components/articles/article-modal';
import BookmarkCard from './bookmark-card';

export default function BookmarkList() {
  const [articles, setArticles] = useState<Article[]>();
  const [page, setPageState] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  // eslint-disable-next-line
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [showDeleteBookmarkModal, setShowDeleteBookmarkModal] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<string>();

  const requestedPageRef = useRef(0);
  const totalPagesRef = useRef(1);

  const fetchData = (page: number) => {
    requestedPageRef.current = page;
    setLoading(true);
    opinionsApi.getArticlesByOpinion('BOOKMARK', { page }).then(
      value => {
        if (requestedPageRef.current !== page) return;
        setArticles(value.content);
        setTotalPages(value.totalPages);
        totalPagesRef.current = value.totalPages;
        setForbidden(false);
        setLoading(false);
      },
      reason => {
        if (requestedPageRef.current !== page) return;
        setForbidden(true);
        setLoading(false);
      },
    );
  };

  const setPage = (p: number) => {
    const clamped = Math.max(0, Math.min(p, totalPagesRef.current - 1));
    setPageState(clamped);
    fetchData(clamped);
  };

  const deleteBookmark = async (uri: string) => {
    await opinionsApi.removeOpinion(uri, 'BOOKMARK');
    const opinions = await opinionsApi.getOpinionsOfArticles(uri);

    if (!opinions[uri]) opinions[uri] = [];

    messageService.publish(MessageServiceConstants.BOOKMARKS_CHANGED, opinions);
    fetchData(page);
  };

  useEffect(() => {
    fetchData(0);
  }, []);

  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useSubscription(MessageServiceConstants.BOOKMARKS_CHANGED, message => {
    fetchData(pageRef.current);
  });

  useTimer(
    30000,
    () => {
      fetchData(pageRef.current);
    },
    false,
  );

  return (
    <div>
      {totalPages > 1 && (
        <div
          className={'d-flex justify-content-center gap-2 pt-2 pb-2 flex-grow-0 align-items-center'}
        >
          <button
            type={'button'}
            className={'btn chevron kk btn-menu'}
            style={{ fontFamily: 'Inter' }}
            disabled={loading || page === 0}
            onClick={() => setPage(page - 1)}
          >
            <FontAwesomeIcon icon={faCaretLeft} size={'lg'} />
          </button>
          <span className={'text-muted'}>
            Page {page + 1} of {totalPages}
          </span>
          <button
            type={'button'}
            className={'btn chevron btn-menu'}
            disabled={loading || page + 1 >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            <FontAwesomeIcon icon={faCaretRight} size={'lg'} />
          </button>
        </div>
      )}

      <div className={`${styles.bookmarkContent} mt-3`}>
        {(!articles || loading) && (
          <div className={'h-100 d-flex align-items-center justify-content-center'}>
            <GridLoader color={theme.info} />
          </div>
        )}
        {!loading && articles && articles.length === 0 && (
          <div
            className={'h-100 d-flex align-items-center justify-content-center text-center small'}
          >
            <span>No bookmarks.</span>
          </div>
        )}
        {!loading && !!articles?.length && (
          <div className={styles.bookmarkGrid}>
            {articles.map(value => (
              <BookmarkCard
                key={'bm-' + value.uri}
                item={value}
                onOpen={() => setSelectedArticle(value.uri)}
                onDelete={() => setShowDeleteBookmarkModal(value.uri)}
              />
            ))}
          </div>
        )}
      </div>

      <DeleteBookmarkModal
        showDeleteBookmarkModal={!!showDeleteBookmarkModal}
        setShowDeleteBookmarkModal={setShowDeleteBookmarkModal}
        onConfirm={() => deleteBookmark(showDeleteBookmarkModal || '')}
      />
      <ArticleModal articleId={selectedArticle} onClose={() => setSelectedArticle(undefined)} />
    </div>
  );
}
